import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import Header from '@/components/Header'
import { LoadingScreen } from '@/components/loading-screen'
import { Images } from '@/assets/Images'
import { useReactToJobDescription } from '@/hooks/mutations/useReactToJobDescription'
import { useSendInvitation } from '@/hooks/mutations/useSendInvitation'
import { ReactionType } from '@/interfaces/reaction'
import { useQueryClient } from '@tanstack/react-query'
import { getContributorText, getReactionText, handleError } from '@/utils/helpers'
import { BookCopyIcon, DownloadIcon, PencilIcon, UserPlusIcon } from 'lucide-react'
import { toPng } from 'html-to-image'
import { JDCard } from '@/components/JDCard'
import { JobDescription } from '@/interfaces/jobDescription'
import { InviteUserDialog } from '@/components/InviteUserDialog'
import { User } from '@/interfaces/user'
import { useGetSurveys } from '@/hooks/queries/useSurveys'
import { SurveyType } from '@/interfaces/survey'
import { toast } from 'react-toastify'
import { ReactionModal } from '@/components/ReactionModal'

interface JDDetailViewProps {
  jobDescription: JobDescription | undefined
  isLoading: boolean
  error: Error | null
  isMyJD?: boolean
  queryKey: string[]
  userName?: string
}

export const JDDetailView = ({
  jobDescription,
  isLoading,
  error,
  isMyJD = false,
  queryKey,
  userName,
}: JDDetailViewProps) => {
  const navigate = useNavigate()
  const { mutate: reactToJD } = useReactToJobDescription()
  const { mutate: sendInvitation, isPending: isInviting } = useSendInvitation()
  const queryClient = useQueryClient()
  const cardRef = useRef<HTMLDivElement>(null)
  const [showAllContributors, setShowAllContributors] = useState(false)
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [isReactionModalOpen, setIsReactionModalOpen] = useState(false)
  const { data: sendJDSurvey } = useGetSurveys(SurveyType.SEND_JD)

  const handleDownload = async () => {
    if (!cardRef.current) return

    try {
      const dataUrl = await toPng(cardRef.current, {
        includeQueryParams: true,
        quality: 1,
        pixelRatio: 1,
        fetchRequestInit: {
          method: 'GET',
          cache: 'no-cache',
        },
      })

      // Create download link
      const link = document.createElement('a')
      link.download = `untold-jd-${userName || jobDescription?.user?.name || 'momo'}.png`
      link.href = dataUrl
      link.click()
    } catch (error) {
      console.error('Error generating image:', error)
    }
  }

  const handleReact = (reactionType: ReactionType) => {
    if (!jobDescription) return

    // Call API and invalidate query to update counts
    reactToJD(
      {
        jobDescriptionId: jobDescription.id,
        reactionType,
      },
      {
        onSuccess: () => {
          // Invalidate the query to refetch updated data
          queryClient.invalidateQueries({ queryKey })
        },
      }
    )
  }

  const handleKeywordClick = (keyword: string) => {
    // Navigate to library page with keyword filter
    navigate(`/library-jd?keywords=${encodeURIComponent(keyword)}`)
  }

  const handleWriteJDForPerson = () => {
    if (!jobDescription?.user) return
    // Navigate to send-jd page with the user ID in state
    navigate('/send-jd', {
      state: {
        targetUserId: jobDescription.user.id,
        targetUserName: jobDescription.user.name,
      },
    })
  }

  const handleInviteUser = (invitee: User) => {
    if (!jobDescription || !sendJDSurvey) return

    sendInvitation(
      {
        inviteeUserId: invitee.id,
        targetUserId: jobDescription.userId,
        surveyId: sendJDSurvey.id,
      },
      {
        onSuccess: () => {
          setShowInviteDialog(false)
          toast.success(`Đã gửi lời mời đến ${invitee.name}`)
        },
        onError: (error) => {
          handleError(error, { fallbackMessage: 'Không thể gửi lời mời' })
        },
      }
    )
  }

  return (
    <div className="relative flex flex-col w-full min-h-screen">
      {/* Background with pattern - Fixed */}
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${Images.bg_with_pattern})`,
          backgroundSize: 'cover',
        }}
      />

      {/* Header */}
      <div className="relative z-10">
        <Header />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center flex-1 w-full py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="py-12 text-center">
            <LoadingScreen message="Đang tải JD..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="max-w-5xl px-4 mx-auto">
            <div className="rounded-3xl p-6 py-12 text-center bg-white shadow-md">
              <p className="mb-6 text-lg font-medium text-red-500">Lỗi: {error.message}</p>
              <Button onClick={() => navigate(-1)} className="btn-primary">
                Quay lại
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {!isLoading && !error && jobDescription && (
          <div className="max-w-5xl mx-auto">
            <div className="animate-in fade-in zoom-in-95 duration-500">
              <JDCard
                ref={cardRef}
                name={userName || jobDescription.user?.name || 'MoMoer'}
                role={jobDescription.role}
                userRole={jobDescription.user?.role}
                notableQualities={jobDescription.notableQualities || ''}
                mainResponsibilities={jobDescription.mainResponsibilities}
                requirements={jobDescription.requirements}
                isMyJD={isMyJD}
                showRibbon={true}
                hasContributors={
                  jobDescription.contributors && jobDescription.contributors.length > 0
                }
              />
            </div>

            {/* Community Section - Separate Panel */}
            <div className="rounded-3xl p-6 mt-6 space-y-4 bg-white shadow-md">
              {/* Reaction Text */}
              {getReactionText(jobDescription) && (
                <div
                  className="hover:text-gray-700 flex items-start gap-1 mb-4 text-xs text-gray-500 cursor-pointer"
                  onClick={() => setIsReactionModalOpen(true)}
                >
                  {getReactionText(jobDescription)!.iconType === 'both' ? (
                    <>
                      <img src={Images.icon_haha} alt="haha" className="w-4 h-4" />
                      <img src={Images.icon_heart} alt="heart" className="w-4 h-4" />
                    </>
                  ) : (
                    <img
                      src={
                        getReactionText(jobDescription)!.iconType === 'heart'
                          ? Images.icon_heart
                          : Images.icon_haha
                      }
                      alt={getReactionText(jobDescription)!.iconType}
                      className="w-3 h-3"
                    />
                  )}
                  <span className="text-xs">
                    {getReactionText(jobDescription)!.names.join(', ')}
                    {getReactionText(jobDescription)!.remaining > 0 &&
                      ` +${getReactionText(jobDescription)!.remaining}`}
                  </span>
                </div>
              )}

              {/* Badges and Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-3">
                  {/* Heart Badge */}
                  <Button
                    onClick={() => handleReact(ReactionType.HEART)}
                    className="border border-primary hover:bg-[#FFEFF4] flex items-center gap-2 bg-white rounded-full px-4 py-2 cursor-pointer transition-colors"
                  >
                    <img src={Images.icon_heart} alt="heart" className="w-6 h-6" />
                    <span className="text-primary text-sm font-semibold">
                      {jobDescription?.heartCount || 0}
                    </span>
                  </Button>
                  {/* Smile Badge */}
                  <Button
                    onClick={() => handleReact(ReactionType.HAHA)}
                    className="border border-primary hover:bg-[#FFEFF4] flex items-center gap-2 bg-white rounded-full px-4 py-2 cursor-pointer transition-colors"
                  >
                    <img src={Images.icon_haha} alt="haha" className="w-6 h-6" />
                    <span className="text-primary text-sm font-semibold">
                      {jobDescription?.hahaCount || 0}
                    </span>
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  {isMyJD ? (
                    <Button onClick={() => setShowInviteDialog(true)} className="btn-primary w-fit">
                      <UserPlusIcon className="size-6" />
                      Mời đồng đội viết cho JD này
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowInviteDialog(true)}
                        className="btn-primary w-fit"
                      >
                        <UserPlusIcon className="size-6" />
                        Mời đồng đội viết cho JD này
                      </Button>
                      <Button onClick={handleWriteJDForPerson} className="btn-primary w-fit">
                        <PencilIcon className="size-6" />
                        Viết JD cho người này
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="my-8 border-t border-gray-200" />

              {/* Contributors Text */}
              {getContributorText(jobDescription, showAllContributors) && (
                <div className="flex items-start justify-between gap-4">
                  <p className="text-primary text-sm font-semibold">
                    {getContributorText(jobDescription, showAllContributors)}
                  </p>
                  {jobDescription.contributors && jobDescription.contributors.length > 3 && (
                    <button
                      onClick={() => setShowAllContributors(!showAllContributors)}
                      className="hover:underline text-primary flex-shrink-0 text-sm font-medium"
                    >
                      {showAllContributors ? 'Thu gọn' : 'Xem thêm'}
                    </button>
                  )}
                </div>
              )}

              {/* Category Tags */}
              {jobDescription.keywords && jobDescription.keywords.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {jobDescription.keywords.map((keyword, index) => (
                    <button
                      key={index}
                      onClick={() => handleKeywordClick(keyword)}
                      className="btn-keyword"
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Download Button */}
            <div className="flex justify-center gap-4 mt-6">
              <Button onClick={handleDownload} className="btn-primary w-fit">
                <DownloadIcon className="size-6" />
                Download
              </Button>
              <Button onClick={() => navigate(`/library-jd`)} className="btn-secondary w-fit">
                <BookCopyIcon className="size-6" />
                Untold JD của MoMoer
              </Button>
            </div>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !error && !jobDescription && (
          <div className="max-w-5xl px-4 mx-auto">
            <div className="rounded-3xl p-6 py-12 text-center bg-white shadow-md">
              <p className="mb-6 text-lg text-gray-700">
                {isMyJD ? 'Bạn chưa có Untold JD nào' : 'Không tìm thấy JD này'}
              </p>
              <Button
                onClick={() => {
                  if (isMyJD) {
                    navigate('/create-jd')
                  } else {
                    navigate(-1)
                  }
                }}
                className="btn-primary"
              >
                {isMyJD ? 'Tạo JD của bạn' : 'Quay lại'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onInvite={handleInviteUser}
        isInviting={isInviting}
      />

      {/* Reaction Modal */}
      <ReactionModal
        isOpen={isReactionModalOpen}
        onClose={() => setIsReactionModalOpen(false)}
        jobDescription={jobDescription}
      />
    </div>
  )
}
